import React, {FC, useEffect, useRef, useState} from "react"
import {CardNumber, ExpiryDate, Cvv, Frames} from "frames-react"
import styled, {css} from "styled-components"

import mainTheme from "@irati/ui/src/global/theme"
import Flex from "@irati/ui/src/Flex"
import {hangPromise} from "@irati/utils/promise"
import {FormattedCurrency, FormattedMessage} from "@irati/utils/lang"
import {Body} from "@irati/ui/src/Typography"
import {PaymentCurrencyType, PaymentSavedCard, RequestPaymentDetailsBillingAddress} from "@irati/types"
import Loading from "@irati/ui/src/Loading"
import {Checkbox, Input, Select, ValidationButton} from "@irati/ui/src/FormItem"
import {Row, Col} from "@irati/ui/src/Layout"
import {useRequestPaymentMutation} from "@irati/payments/hooks"
import Spacing from "@irati/ui/src/Spacing"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import env from "@irati/utils/env"
import {PRODUCTION} from "@irati/utils/constants/environment"
import countries from "@irati/region/helpers/isoCountryCodes.json"
import Validation from "@irati/utils/validation"

import getCircuitLogo from "../helpers/getCircuitLogo"
import useExternalScript, {ScriptState} from "./useExternalScript"

interface Props {
  amount: number
  paymentOrderId: string
  currency: PaymentCurrencyType
  returnUrl: string
  onComplete: () => void
  setLoad: (value: Promise<any>) => void
  publicKey: string
  load?: Promise<any>
  savedCards: PaymentSavedCard[]
  circuits?: string[]
}

const CardsComponent: FC<Props> = ({
  savedCards,
  amount,
  currency,
  paymentOrderId,
  returnUrl,
  onComplete,
  load,
  setLoad,
  publicKey,
  circuits = []
}) => {
  const [billingAddress, _setBillingAddress] = useState<RequestPaymentDetailsBillingAddress>()
  const [cvv, setCvv] = useState<string>()
  const [rememberCard, _setRememberCard] = useState<boolean>(false)
  const [cardholderName, setCardholderName] = useState<string>()
  // If use has saved cards, select first one by default, select new card otherwise.
  const defaultSelectedCard = savedCards.length > 0 ? 0 : newCardIndex
  const [selectedCard, setSelectedCard] = useState<number>(defaultSelectedCard)
  const [requestPayment] = useRequestPaymentMutation()
  const [deviceSessionId, setDeviceSessionId] = useState<string>()
  const riskJsState = useExternalScript(getRiskJsUrl())

  useEffect(() => {
    const getDeviceSessionId = async () => {
      if (publicKey && riskJsState === ScriptState.Ready) {
        const risk = window.Risk.init(publicKey)
        setDeviceSessionId(await risk.publishRiskData())
      }
    }
    getDeviceSessionId()
  }, [publicKey, riskJsState])

  // Frames is based on eventListeners, which point at the first version of the handler defined when the component mounts.
  // In our case, the state of the component changes, so we need to use references.
  const amountRef = useRef(amount)
  useEffect(() => {
    amountRef.current = amount
  }, [amount])
  const rCRef = useRef(rememberCard)
  const setRememberCard = (value) => {
    rCRef.current = value
    _setRememberCard(value)
  }
  const bARef = useRef(billingAddress)
  const setBillingAddress = (key: string, value: string) => {
    bARef.current = {...bARef.current, [key]: value}
    _setBillingAddress((prevState) => ({...prevState, [key]: value}))
  }

  const payBySavedCard = async (cardId: string) => {
    const variables = {
      input: {
        paymentOrderId,
        amount: amountRef.current,
        currency,
        details: {cardId, cvv, resURL: returnUrl, deviceSessionId}
      }
    }
    await makePayment(variables)
  }

  const handleCardTokenised = async ({token}: {token: string}) => {
    // Use ref values in here, as they access the current state value
    const variables = {
      input: {
        paymentOrderId,
        amount: amountRef.current,
        currency,
        details: {
          token,
          rememberCard: rCRef.current,
          billingAddress: bARef.current,
          resURL: returnUrl
        }
      }
    }
    await makePayment(variables)
  }

  const makePayment = async (variables) => {
    const req = requestPayment({variables})
    setLoad(req)
    const payment = await req
    setLoad(hangPromise())

    if (payment.details.url) {
      // 3DS redirect
      window.location.href = payment.details.url
    } else {
      onComplete()
    }
  }

  const handleCardSelection = (i) => {
    setSelectedCard(i)
    setCvv(undefined)
  }

  const isNewCardSelected = selectedCard === newCardIndex
  return (
    <OptionWrapper>
      {/* Saved Cards */}
      {savedCards.map((card, i) => {
        const isCardSelected = selectedCard === i
        return (
          <Option key={`card${i}`} isSelected={isCardSelected}>
            <PayBySavedCardHeader savedCard={card} onClick={() => handleCardSelection(i)} isOpen={isCardSelected} />
            {isCardSelected && (
              <Spacing padding={[0, 2, 2]}>
                <Row>
                  <Col md={6}>
                    <Row>
                      <Input name="expiry" label="Expiry date" disabled value={`${card.expiry?.month}/${card.expiry?.year}`} />
                    </Row>
                  </Col>
                  <Col md={6}>
                    <Row>
                      <Input
                        name="cvv"
                        label="CVC / CVV"
                        value={cvv}
                        onChange={(v) => setCvv(v)}
                        validation={Validation.string("platform.defaultValidation.required")}
                      />
                    </Row>
                  </Col>
                </Row>
                <ValidationButton onClick={() => payBySavedCard(card.reference!)} full primary marginTop={2} load={load}>
                  <FormattedMessage id="platform.payments.pay" />
                  &nbsp;
                  <FormattedCurrency value={amount} currency={currency} />
                </ValidationButton>
                <Loading load={load} useLoader={false} />
              </Spacing>
            )}
          </Option>
        )
      })}

      {/* New Card */}
      <Option isSelected={isNewCardSelected}>
        <PayByCardHeader onClick={() => setSelectedCard(newCardIndex)} isOpen={isNewCardSelected} circuits={circuits} />
        {isNewCardSelected && (
          <Spacing padding={[0, 2, 2]}>
            <Row>
              <Input
                name="cardholderName"
                label="platform.payments.billingAddress.cardholderName"
                value={cardholderName}
                validation={Validation.string("platform.defaultValidation.required")}
                onChange={(v) => setCardholderName(v)}
              />
            </Row>
            <Frames config={{publicKey, cardholder: {name: cardholderName}, style: framesStyle}} cardTokenized={handleCardTokenised}>
              <Flex>
                <CustomLabel>
                  <FormattedMessage id="platform.payments.cardNumber" />
                </CustomLabel>
              </Flex>
              <FramesInputWrapper>
                <CardNumber />
              </FramesInputWrapper>
              <Flex gap={2} marginTop={2}>
                <div style={{flexGrow: 1}}>
                  <CustomLabel>
                    <FormattedMessage id="platform.payments.expiryDate" />
                  </CustomLabel>
                </div>
                <div style={{flexGrow: 1}}>
                  <CustomLabel>
                    <FormattedMessage id="platform.payments.cvv" />
                  </CustomLabel>
                </div>
              </Flex>
              <FramesInputWrapper gap={2}>
                <ExpiryDate />
                <Cvv />
              </FramesInputWrapper>

              <Row marginTop={2}>
                <Checkbox name="isAdmin" checked={rememberCard} onChange={() => setRememberCard(!rememberCard)}>
                  <FormattedMessage id="platform.payments.storeCard" />
                </Checkbox>
              </Row>

              <Body marginTop={3} marginBottom={3}>
                <FormattedMessage id="platform.payments.billingAddress.header" />
              </Body>

              <Row>
                <Select
                  label="platform.payments.billingAddress.country"
                  name="country"
                  value={billingAddress?.state}
                  options={stateOptions}
                  validation={Validation.string("platform.defaultValidation.required")}
                  onChange={(v) => setBillingAddress("country", v)}
                />
              </Row>
              <Row>
                <Input
                  name="addressLine1"
                  label="platform.payments.billingAddress.line1"
                  value={billingAddress?.addressLine1}
                  validation={Validation.string("platform.defaultValidation.required")}
                  onChange={(v) => setBillingAddress("addressLine1", v)}
                />
              </Row>
              <Row>
                <Input
                  name="addressLine2"
                  label="platform.payments.billingAddress.line2"
                  value={billingAddress?.addressLine2}
                  onChange={(v) => setBillingAddress("addressLine2", v)}
                />
              </Row>
              <Row>
                <Col md={6}>
                  <Row>
                    <Input
                      name="zip"
                      label="platform.payments.billingAddress.postcode"
                      value={billingAddress?.zip}
                      validation={Validation.string("platform.defaultValidation.required")}
                      onChange={(v) => setBillingAddress("zip", v)}
                    />
                  </Row>
                </Col>
                <Col md={6}>
                  <Row>
                    <Input
                      name="city"
                      label="platform.payments.billingAddress.city"
                      value={billingAddress?.city}
                      validation={Validation.string("platform.defaultValidation.required")}
                      onChange={(v) => setBillingAddress("city", v)}
                    />
                  </Row>
                </Col>
              </Row>
              <ValidationButton
                primary
                full
                marginTop={2}
                load={load}
                onClick={() => {
                  if (Frames.isCardValid()) {
                    setLoad(hangPromise())
                    Frames.submitCard()
                  } else {
                    setLoad(Promise.reject({message: "errors.PBERRInvalidCardDetails"}))
                  }
                }}>
                <FormattedMessage id="platform.payments.pay" />
                &nbsp;
                <FormattedCurrency value={amount} currency={currency} />
              </ValidationButton>
              <Loading load={load} useLoader={false} />
            </Frames>
          </Spacing>
        )}
      </Option>
    </OptionWrapper>
  )
}

export default CardsComponent

const newCardIndex = -1

const CustomLabel = styled.label`
  font-family: WorkSans, sans-serif;
  font-size: 14px;
`

const TabbableFlex = styled(Flex).attrs({tabIndex: 0, as: "button"})`
  border: 0;
  background: none;
  cursor: pointer;
  width: 100%;
`

const FramesInputWrapper = styled(Flex)`
  height: 42px;
  div {
    flex-grow: 1;
  }
`

const PayBySavedCardHeader = ({savedCard, onClick, isOpen}: {savedCard: PaymentSavedCard; onClick: () => any; isOpen: boolean}) => (
  <TabbableFlex padding={2} onClick={onClick} justifyContent="space-between" alignItems="center">
    <Body>
      {getCircuitLogo(savedCard.variant)} •••• {savedCard.number}
    </Body>
    <Icon icon={isOpen ? IconTypes.chevronUp : IconTypes.chevronDown} size={20} />
  </TabbableFlex>
)

const PayByCardHeader = ({onClick, isOpen, circuits}: {onClick: () => any; isOpen: boolean; circuits: string[]}) => (
  <TabbableFlex padding={2} onClick={onClick} justifyContent="space-between" alignItems="center">
    <Flex alignItems="center">
      <Icon icon={IconTypes.payByCard} marginRight size={24} />
      <Body>
        <FormattedMessage id="platform.payments.payByCard" />
      </Body>
    </Flex>
    <Flex alignItems="center">
      <Flex alignItems="center" gap={1} marginRight>
        {circuits.map((c) => getCircuitLogo(c))}
      </Flex>
      <Icon icon={isOpen ? IconTypes.chevronUp : IconTypes.chevronDown} size={20} />
    </Flex>
  </TabbableFlex>
)

const Option = styled.div<{isSelected?: boolean}>`
  ${({isSelected, theme}) => (isSelected ? `background-color: ${theme.colors.greys.grey03};` : "")}
`

const OptionWrapper = styled.div`
  ${Option} {
    ${({theme}) => css`
      border: 1px solid ${theme.colors.greys.grey20};
      border-bottom: 0;
      &:first-child {
        border-radius: ${theme.border.radius.large} ${theme.border.radius.large} 0 0;
      }
      &:last-child {
        border-radius: 0 0 ${theme.border.radius.large} ${theme.border.radius.large};
        border-bottom: 1px solid ${theme.colors.greys.grey20};
      }
      &:only-child {
        border-radius: ${theme.border.radius.large};
        border-bottom: 1px solid ${theme.colors.greys.grey20};
      }
    `}
  }
`

const framesStyle = {
  base: {
    fontFamily: mainTheme.font.light,
    letterSpacing: "normal",
    background: mainTheme.colors.greys.white,
    border: `1px solid ${mainTheme.colors.greys.grey20}`,
    color: mainTheme.colors.greys.black,
    borderRadius: mainTheme.border.radius.small,
    paddingLeft: mainTheme.spacing.one,
    width: "100%"
  }
}

const getRiskJsUrl = () => {
  if (env.ENVIRONMENT_NAME === PRODUCTION) {
    return "https://risk.checkout.com/cdn/risk/1/risk.js"
  }
  return "https://risk.sandbox.checkout.com/cdn/risk/1/risk.js"
}

const stateOptions = Object.keys(countries).map((key) => ({
  label: key,
  value: countries[key]
}))
